// SceneImagePreview.jsx
// This component is used to display a preview of a scene image in the file viewer.

// Copyright HS Analysis GmbH, 2025
// Authors: Jakob Burghardt, Viktor Eberhardt

// Framework imports
import React from "react";
import PropTypes from "prop-types";

// MUI imports
import { LazyLoadImage } from "react-lazy-load-image-component";
import { makeStyles, useTheme } from "@mui/styles";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

// HSA Imports
import Backend from "../../common/utils/Backend";

const useStyles = makeStyles(() => ({
  imgWrapper: {
    display: "flex",
    position: "relative",
    width: "100%",
    flexDirection: "column",
    alignItems: "center",
    border: "1px solid #ddd",
    borderRadius: 4,
    padding: 5,
    backgroundColor: "#f9f9f9",
    pointerEvents: "none",
  },
  imgLabel: {
    fontSize: 12,
    marginTop: 5,
    color: "#555",
    wordWrap: "break-word",
    width: "100%",
    maxWidth: "190px",
    overflow: "hidden",
  },
  sceneAdditionalName: {
    fontSize: 12,
    marginTop: 5,
    color: "#888",
    wordWrap: "break-word",
    width: "100%",
    maxWidth: "190px",
    overflow: "hidden",
  },
  sceneComment: {
    fontSize: 12,
    marginTop: 5,
    color: "#888",
    wordWrap: "break-word",
    width: "100%",
    maxWidth: "190px",
    overflow: "hidden",
  },
  img: {
    display: "inline-block",
    cursor: "pointer",
    width: "100%",
    maxHeight: "200px",
    objectFit: "contain",
  },
  placeholder: {
    height: 200,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  lazyImage: {
    objectFit: "contain",
    width: "100%",
  },
  ellipsisText: {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    display: "block",
    alignItems: "center",
    maxWidth: "150px",
    position: "relative",
    paddingRight: "16px",
  },
  infoIcon: {
    position: "absolute",
    right: 0,
    top: 2,
    marginLeft: 4,
    fontSize: 16,
    color: "#888",
    padding: 0,
    pointerEvents: "auto",
  },
  hasSeenDot: {
    position: "absolute",
    top: 5,
    right: 5,
    width: 10,
    height: 10,
    borderRadius: "50%",
  },
}));

const SceneImagePreview = ({
  scene,
  width,
  showAdditionalName,
  showComments,
  fileStatus,
  isTruncated,
  setDialogTitle,
  setDialogText,
  setDialogOpen,
  handleImageError,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  let fileStatusBorder = "";
  if (fileStatus === "imgActive") {
    fileStatusBorder = `solid 3px ${theme.palette.primary.main}`;
  } else if (fileStatus === "imgSplitScreen") {
    fileStatusBorder = `dashed 3px ${theme.palette.primary.main}`;
  }

  return (
    <div className={classes.imgWrapper}>
      {showAdditionalName &&
        scene.additionalName &&
        scene.additionalName !== "" && (
          <div
            className={`${classes.sceneAdditionalName} ${classes.ellipsisText}`}
            title={scene.additionalName}
            ref={(el) => {
              if (el && isTruncated(el))
                el.setAttribute("data-truncated", "true");
              else if (el) el.removeAttribute("data-truncated");
            }}
          >
            {scene.additionalName}
            <span>
              <IconButton
                className={classes.infoIcon}
                size="small"
                style={{
                  visibility: isTruncated(
                    document.querySelector(`[title="${scene.additionalName}"]`)
                  )
                    ? "visible"
                    : "hidden",
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  setDialogTitle("Additional Name");
                  setDialogText(scene.additionalName);
                  setDialogOpen(true);
                }}
                tabIndex={-1}
              >
                <InfoOutlinedIcon fontSize="inherit" />
              </IconButton>
            </span>
          </div>
        )}
      <div className={classes.imgLabel}>
        {scene.path.split(/[/\\]/).pop()}
        {scene.sceneCount > 1 && ", S" + (scene.scene + 1)}
      </div>
      <LazyLoadImage
        key={scene.path + scene.id + "lazy"}
        height={200}
        placeholder={
          <div className={classes.placeholder}>
            <CircularProgress />
          </div>
        }
        src={Backend.renderThumbnail(encodeURIComponent(scene.id))}
        alt="Preview Image"
        className={classes.img}
        style={{ border: fileStatusBorder, width: width }}
        onError={() => {
          handleImageError(scene);
        }}
      />
      {showComments && scene.comment && scene.comment !== "" && (
        <div
          className={`${classes.sceneComment} ${classes.ellipsisText}`}
          title={scene.comment}
          ref={(el) => {
            if (el && isTruncated(el))
              el.setAttribute("data-truncated", "true");
            else if (el) el.removeAttribute("data-truncated");
          }}
        >
          {scene.comment}
          <span>
            <IconButton
              className={classes.infoIcon}
              size="small"
              style={{
                visibility: isTruncated(
                  document.querySelector(`[title="${scene.comment}"]`)
                )
                  ? "visible"
                  : "hidden",
              }}
              onClick={(e) => {
                e.stopPropagation();
                setDialogTitle("Comment");
                setDialogText(scene.comment);
                setDialogOpen(true);
              }}
              tabIndex={-1}
            >
              <InfoOutlinedIcon fontSize="inherit" />
            </IconButton>
          </span>
        </div>
      )}
    </div>
  );
};

SceneImagePreview.propTypes = {
  scene: PropTypes.object.isRequired,
  showAdditionalName: PropTypes.bool,
  showComments: PropTypes.bool,
  fileStatus: PropTypes.string.isRequired,
  isTruncated: PropTypes.func.isRequired,
  setDialogTitle: PropTypes.func.isRequired,
  setDialogText: PropTypes.func.isRequired,
  setDialogOpen: PropTypes.func.isRequired,
  handleImageError: PropTypes.func.isRequired,
  width: PropTypes.string,
};

// set default props for optional fields
SceneImagePreview.defaultProps = {
  showAdditionalName: false,
  showComments: false,
  fileStatus: "",
  isTruncated: () => false,
  setDialogTitle: () => {},
  setDialogText: () => {},
  setDialogOpen: () => {},
  handleImageError: () => {},
};

export default SceneImagePreview;
